"use client"

import * as React from "react"
import {
    ColumnDef,
} from "@tanstack/react-table"
import { MoreVerticalIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import CommonTableList from "@/components/common/TableList"
import IncomingsCreate from "./Create"
import CommonDelete from "@/components/common/Delete"
import IncomingsEdit from "./Edit"
import { IncomeData } from "@/interface/entityInterface"
import { IncomeDetails } from "./IncomesDetails"
import { format } from "date-fns"
import CommonList from "@/components/common/List"

export const IncomesList = () => {
    const [open, setOpen] = React.useState(false)
    const [income, setIncome] = React.useState<IncomeData | null>(null)

    const handleOpen = (e: any, row: IncomeData) => {
        setOpen(true)
        setIncome(row)
    }

    const columns = React.useMemo<ColumnDef<IncomeData>[]>(() => [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    checked={
                        table.getIsAllPageRowsSelected() ||
                        (table.getIsSomePageRowsSelected() && "indeterminate")
                    }
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label="Select all"
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label="Select row"
                />
            ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "id",
            header: "ID",
            cell: ({ row }) => <div className="capitalize">{row.getValue("id")}</div>,
        },
        {
            accessorFn: (row) => row?.vehicle.license_plate || "",   
            accessorKey: "vehicle",
            id: "vehicle",
            header: "Vehicle",
            cell: ({ row }) => {
                const vehicle = row.getValue("vehicle") as any;
                return (
                    <div className="flex flex-col text-sm space-y-1">
                        <span className="font-medium">{vehicle}</span>
                    </div>
                );
            },
        },
                {
            accessorFn: (row) => row?.incomings_type.name || "",   
            accessorKey: "incomings_type",
            id: "incomings_type",
            header: "Profit source",
            cell: ({ row }) => {
                const incomingsType = row.getValue("incomings_type") as any;
                return (
                    <div className="flex flex-col text-sm space-y-1">
                        <span className="font-medium">{incomingsType}</span>
                    </div>
                );
            },
        },
        {
            accessorKey: "value",
            header: "Profit value",
            cell: ({ row }) => (
                <div className="flex flex-col text-sm space-y-1">
                    <span className="font-medium">{row.getValue("value")}</span>
                </div>
            ),
        },
        {
            accessorKey: "date",
            header: "Date",
            cell: ({ row }) => (
                <div className="flex flex-col text-sm space-y-1">
                    <span className="font-medium">{format(row.getValue("date"), "dd MMMM yyyy HH:mm")}</span>
                </div>
            ),
        },
                {
            accessorKey: "odometer",
            header: "Odometer",
            cell: ({ row }) => (
                <div className="flex flex-col text-sm space-y-1">
                    <span className="font-medium">{row.getValue("odometer")}</span>
                </div>
            ),
        },
        {
            id: "actions",
            cell: ({row}) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
                            size="icon"
                        >
                            <MoreVerticalIcon />
                            <span className="sr-only">Open menu</span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-32">
                        <IncomingsEdit prop={row.original.id} />
                        <DropdownMenuSeparator />
                        <CommonDelete resource="incomings" id={row.original.id} />
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        }
    ], [])

    return (
        <>
            <CommonList>
                <CommonTableList 
                columns={columns} 
                filterSearch="odometer" 
                create={<IncomingsCreate />} 
                rowClick={handleOpen}
            />
            </CommonList>
            {open && <IncomeDetails open={open} setOpen={setOpen} income={income} />}
        </>
    )
}

export default IncomesList
